import { tryGetSupportedBrowserLocale } from '../locale';
import { Locale } from '../locale/types';
import { Currency } from '../model/money';
import { Countries, CountryMap, type Country } from '../util/msisdn';

export const ApiSource = {
  Unknown: 'Unknown',
  EcomV1: 'EcomV1',
  EPayment: 'EPayment',
  EcomV2: 'EcomV2',
  PSPV3: 'PSPV3',
  MPAppPayments: 'MPAppPayments',
  MPOnline: 'MPOnline',
} as const;
export type ApiSource = (typeof ApiSource)[keyof typeof ApiSource];

export const PaymentType = {
  PAYMENT: 'PAYMENT',
  EXPRESS: 'EXPRESS_CHECKOUT',
  RECURRING: 'RECURRING',
  PSP_PAYMENT: 'PSP_PAYMENT',
} as const;
export type PaymentType = (typeof PaymentType)[keyof typeof PaymentType];

export const Brand = {
  VIPPS: 'VIPPS',
  MOBILEPAY: 'MOBILEPAY',
} as const;
export type Brand = (typeof Brand)[keyof typeof Brand];

export function prettyPrintBrand(brand: Brand): string {
  switch (brand) {
    case Brand.VIPPS:
      return 'Vipps';
    case Brand.MOBILEPAY:
      return 'MobilePay';
  }
}

export function getDefaultCountry(claims: ParsedClaims | null): Countries {
  if (claims == null) {
    return Countries.Norway;
  }

  if (claims.apiSource === ApiSource.MPOnline) {
    switch (tryGetSupportedBrowserLocale()) {
      case Locale.NO:
        return Countries.Norway;
      case Locale.DK:
        return Countries.Denmark;
      case Locale.FI:
        return Countries.Finland;
      default: {
        // continue below
        break;
      }
    }
  }

  switch (claims.market) {
    case Market.NO:
      return Countries.Norway;
    case Market.DK:
      return Countries.Denmark;
    case Market.FI:
      return Countries.Finland;
    default:
      return Countries.Norway;
  }
}

export function getSelectableCountriesForBrand(brand: Brand): Countries[] {
  switch (brand) {
    case Brand.VIPPS:
      return [Countries.Norway, Countries.Sweden];
    case Brand.MOBILEPAY:
      return [Countries.Denmark, Countries.Finland, Countries.Greenland];
  }
}

export function getSelectableCountries(claims: ParsedClaims | null): Country[] {
  if (claims?.paymentType === PaymentType.PSP_PAYMENT) {
    return getSelectableCountriesForBrand(claims.brand).map((country) => CountryMap[country]);
  }
  return Object.values(CountryMap);
}

export function getBrandFromQueryParamOrClaimsOrHost(
  claims: ParsedClaims | null | undefined
): Brand {
  const urlParams = new URLSearchParams(window.location.search);
  const brandParam = urlParams.get('brand');

  if (brandParam?.toUpperCase() === 'VIPPS') {
    return Brand.VIPPS;
  }
  if (brandParam?.toUpperCase() === 'MOBILEPAY') {
    return Brand.MOBILEPAY;
  }

  if (claims && claims.brand) {
    return claims.brand;
  }

  return getBrandFromUrl();
}

export function getBrandFromUrl() {
  const hostname = window.location.hostname;
  if (hostname.toLowerCase().includes('mobilepay')) {
    return Brand.MOBILEPAY;
  }
  return Brand.VIPPS;
}

export const Market = {
  NO: 'NO',
  DK: 'DK',
  FI: 'FI',
  SE: 'SE',
} as const;
export type Market = (typeof Market)[keyof typeof Market];

export interface ParsedClaims {
  shouldTryDeeplink: boolean;
  shouldTryFancyDeeplink: boolean;
  shouldCancelOnBackNav: boolean;
  url: string;
  fallback: string;
  mobile: string;
  expiryTime: number;
  noEdit: boolean;
  paymentType: PaymentType;
  issuedAt: number;
  title: string;
  market: Market;
  brand: Brand;
  currency: Currency;
  amount: number;
  apiSource: ApiSource;
  shouldTrackMixpanel: boolean;
  blockPhoneNumberInput: boolean;
}

const timeLeftMs = 1000 * 1000;

export const testClaims: ParsedClaims = {
  shouldTryDeeplink: true,
  shouldTryFancyDeeplink: true,
  shouldCancelOnBackNav: true,
  url: 'https://api.vipps.no/landingpage/dwo/v1/landingpage',
  fallback: 'https://vipps.no/',
  mobile: '',
  expiryTime: (Date.now() + timeLeftMs) / 1000,
  noEdit: false,
  paymentType: PaymentType.PAYMENT,
  issuedAt: Math.floor(Date.now() / 1000),
  title: '120 Kr',
  market: Market.NO,
  brand: Brand.VIPPS,
  currency: Currency.NOK,
  amount: 12000,
  apiSource: ApiSource.MPOnline,
  shouldTrackMixpanel: false,
  blockPhoneNumberInput: false,
};
